/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import Media from "react-media";
import moment from "moment";
import {
  dealDuplicatesReducer,
  getRefundPercent,
  dealCloser
} from "../Chart/Deal";
import Gettext, { withGettext } from "../common/Gettext";
import Refund from "../common/Refund";
import Money from "../Money";
import { tournamentCurrency } from "../Icons/Kuchka";
import { NewSimpleSpinner } from "../Spinners/SimpleSpinner";
import CloseIcon from "@material-ui/icons/Close";

import type { Deal as DealObject } from "../../redux/CommonTypes";
import { fontSize, rtl } from "../common/styled-mixins";

const RefundContainer = styled.span`
  font-size: ${fontSize(10, 10)};

  @media (max-width: 479px) {
    order: 2;
    width: 30%;
    text-align: right;
  }
`;

const Title = styled.div`
  font-size: ${fontSize(14, 14)};
  text-transform: uppercase;

  ${props =>
    props.mobile &&
    css`
      font-size: ${fontSize(9, 9)};
      color: ${({ theme }) => theme.foreground};

      @media (max-width: 479px) {
        order: 0;
        width: 45px;
        margin-right: 5px;
      }
    `};
`;

const OperationView = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: space-between;
  justify-content: space-between;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  height: 68px;
  padding: 10px;
  color: #817c86;
  background-color: ${props => props.theme.dealsBackground};
  background-repeat: no-repeat;
  background-position: 100% 0;
  border-bottom: 1px dashed ${props => props.theme.mainSeparator};
  user-select: none;
  cursor: ${props => (props.isClickable ? "pointer" : "default")};

  :active {
    opacity: 0.75;
  }

  ${Title} {
    transition: color 0.3s ease;
  }

  :hover {
    ${Title} {
      color: ${props => (props.isClickable ? props.theme.accent : "inherit")};
    }
  }

  ${props =>
    props.mobile &&
    css`
      width: 100%;
      height: auto;
      padding: 7px 10px;
      background-color: transparent;
      border-bottom: 1px solid ${props => props.theme.borderMainColor};

      @media (max-width: 479px) {
        padding: 7px 5px;

        ${({ sellEnabled }) =>
          sellEnabled &&
          css`
            flex-wrap: nowrap;
            padding: 0 0 0 5px;
          `};
      }
    `};
`;

const Time = styled.div`
  width: 100%;
  padding-bottom: 6px;
  font-size: ${fontSize(11, 11)};
  line-height: 1;
  text-align: right;
`;

const Bet = styled.div`
  font-size: ${fontSize(14, 14)};
  font-weight: 700;
  text-align: right;
  color: ${({ theme }) => theme.ticksFontColor};

  ${({ theme, isWinning }) => {
    switch (isWinning) {
      case true:
        return css`
          color: ${theme.upper};
        `;
      case false:
        return css`
          color: ${theme.lower};
        `;
      case null:
      default:
        break;
    }
  }};
  ${props =>
    props.mobile &&
    css`
      font-size: ${fontSize(12, 12)};

      @media (max-width: 479px) {
        order: 2;
        width: 30%;
      }
    `};
`;

const BetCount = styled.div`
  display: table;
  width: 100%;
  padding-top: 5px;
  font-weight: 400;
  font-size: ${fontSize(12, 12)};
  line-height: 1;
`;

const Container = styled.div`
  display: table-cell;
  vertical-align: top;
`;

const BetDirection = styled.div`
  display: inline-block;
  vertical-align: middle;
  width: 9px;
  height: 7px;
  margin-right: 4px;
  line-height: 0;
`;

const BetDirectionText = styled.div`
  display: inline-block;
  vertical-align: middle;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  /* color: ${({ theme }) => theme.foreground}; */
  color: #99959d;
`;

const BetCountText = styled.div`
  display: table-cell;
  vertical-align: top;
  padding-top: 1px;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  ${rtl("text-align", "right")};
  /* color: ${({ theme }) => theme.foreground}; */
  color: #99959d;
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: 100%;
  height: 4px;
  background-color: ${props => props.theme.mainLight};

  ${props =>
    props.mobile &&
    css`
      height: 2px;
    `};
`;

const Progress = styled.div`
  height: 4px;
  width: 0%;
  background-color: ${props => props.theme.accent};
  transition: all 0.1s ease;

  ${props =>
    props.mobile &&
    css`
      height: 2px;
    `};
`;

/* Mobile-only thigs */

const DealTypeandTimeView = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: space-between;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 7px;

  @media (max-width: 479px) {
    order: 1;
    width: 45%;
    margin-top: 0;
  }
`;

const TimeOnMobile = styled.div`
  width: auto;
  padding-bottom: 0;
  font-size: ${fontSize(10, 10)};
  line-height: 1;
  text-align: right;
`;

// no open trades
const NoOpenTrades = styled.div`
  padding: 10px;
  font-size: ${fontSize(14)};
  text-align: center;
  color: #99959d;

  @media (max-width: 959px) {
    font-size: ${fontSize(12)};
  }
`;

const BetIconWrapper = styled.span`
  color: ${({ theme, bidType }) => (bidType ? theme.upper : theme.lower)};
`;

const SellDealBlock = styled.div`
  @media (max-width: 479px) {
    margin-left: 10px;
    order: 3;
  }
`;

const SellDealBlockTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px;
  background-color: ${({ theme }) => theme.sellDealHistoryBg};

  @media (max-width: 479px) {
    padding: 5px;
  }
`;

const SellDealTimer = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.sellDealHistoryColor};
`;

const SellDealRight = styled.div`
  display: flex;
  align-items: center;
`;

const SellDealMoney = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.sellDealHistoryColor};

  @media (max-width: 479px) {
    margin-left: 5px;
  }
`;

const SellDealProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: ${({ theme }) => theme.sellDealHistoryProgress};
`;

const SellDealProgress = styled.div`
  position: absolute;
  left: 0;
  height: 100%;
  background-color: ${({ theme }) => theme.sellDealHistoryColor};
`;

const SellDealClose = styled.div`
  margin-left: 5px;
  font-size: 15px;
  color: #919191;
  cursor: pointer;
`;

const BetIcon = ({ bidType }: { bidType: boolean }) => (
  <BetIconWrapper bidType={bidType}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="7"
      viewBox="0 0 9 7"
      fill="currentColor"
    >
      {bidType ? (
        <path d="M4.5,0.494L0.779,6.921H8.226L4.5,0.494" />
      ) : (
        <path d="M4.5,7.384L0.779,0.958H8.226L4.5,7.384" />
      )}
    </svg>
  </BetIconWrapper>
);

const Icon = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="currentColor"
  >
    <path fillRule="evenodd" fillOpacity="0.549" d={props.d} />
  </svg>
);

type DealProps = {
  tradeMode?: "tournament" | "binary",
  mobile?: boolean,
  now: number,
  point: {
    date: number,
    stock: string,
    value: number
  },
  pointsByStock: Object | void,
  time?: number,
  profit?: number
};

export function OpenOperation(props: DealObject & DealProps) {
  if (typeof props.percent !== "number") {
    return null;
  }

  const progress = Math.min(
    (props.now - props.open_timestamp * 1000) / (props.bTime * 1000),
    1
  );

  const sellProgress = Math.min(
    (props.now - props.open_timestamp * 1000) / ((props.bTime * 1000) / 2),
    1
  );

  const sellTime =
    (props.bTime * 1000) / 2 + props.open_timestamp * 1000 - props.now;

  const sellHoursCount = parseInt((sellTime / (1000 * 60 * 60)) % 24, 10);
  const sellMinutesCount = parseInt((sellTime / (1000 * 60)) % 60, 10);
  const sellSecondsCount = parseInt((sellTime / 1000) % 60, 10);

  const sellHours = sellHoursCount >= 1 ? `${sellHoursCount}:` : "";
  const sellMinutes =
    sellMinutesCount < 10 ? `0${sellMinutesCount}` : sellMinutesCount;
  const sellSeconds =
    sellSecondsCount < 10 ? `0${sellSecondsCount}` : sellSecondsCount;

  const sellTimer = `${sellHours}${sellMinutes}:${sellSeconds}`;

  const date = moment(props.open_timestamp * 1000).format("DD/MM HH:mm:ss");
  const quote = props.pointsByStock && props.pointsByStock[props.stock];
  const currency = props.tradeMode === "tournament" ? tournamentCurrency : null;

  const windowWidth = document.documentElement.clientWidth;

  let profit = null;

  if (
    props.now - props.open_timestamp * 1000 >= props.bTime * 1000 ||
    quote == null
  ) {
    profit = <NewSimpleSpinner />;
  } else {
    let deviation = props.bidType
      ? quote.value - props.value
      : props.value - quote.value;

    switch (Math.sign(deviation)) {
      case 1:
        profit = (
          <Bet mobile={props.mobile} isWinning={true}>
            <Money currency={currency}>
              {(props.multiplier * (props.amount * props.percent)) / 100}
            </Money>
          </Bet>
        );
        break;
      case -1:
        profit = (
          <Bet mobile={props.mobile} isWinning={false}>
            <Money currency={currency}>
              {-1 * props.amount * props.multiplier}
            </Money>
          </Bet>
        );
        break;
      default:
        profit = (
          <Bet mobile={props.mobile} isWinning={true}>
            <Money currency={currency}>0</Money>
          </Bet>
        );
        break;
    }
  }

  const current =
    props.pointsByStock &&
    props.pointsByStock[props.stock] &&
    props.pointsByStock[props.stock].value;

  // const features = api.readEntity("/api/features/list");
  // const closeEarly = features.value && features.value.find(feature => feature.name === "operation_early_close");
  const isRefundable =
    props.refund_10 != null &&
    props.refund_30 != null &&
    props.refund_95 != null &&
    props.refund_100 != null &&
    props.refund_105 != null &&
    props.refund_110 != null &&
    props.refund_115 != null;

  let refundProfit;
  if (isRefundable) {
    const returnK = (current - props.value) / props.value;
    const refundPercent = getRefundPercent(returnK, props.bidType, {
      refund_10: props.refund_10,
      refund_30: props.refund_30,
      refund_95: props.refund_95,
      refund_100: props.refund_100,
      refund_105: props.refund_105,
      refund_110: props.refund_110,
      refund_115: props.refund_115
    });

    refundProfit = props.amount * refundPercent;
  }

  const SellDeal = () => {
    if (!isRefundable || sellProgress >= 1) return null;

    return (
      <SellDealBlock>
        <SellDealBlockTop>
          <SellDealTimer>{sellTimer}</SellDealTimer>
          <SellDealRight>
            <SellDealMoney>
              <Money>{props.multiplier * refundProfit}</Money>
            </SellDealMoney>
            <SellDealClose
              onClick={props.refundClickHandler}
              title={
                props.multiplier > 1
                  ? props.gettext`Close ${props.multiplier} deals`
                  : props.gettext`Close the deal`
              }
            >
              {props.refundFetching.has(props.id) ? (
                <NewSimpleSpinner />
              ) : (
                <CloseIcon color="inherit" fontSize="inherit" />
              )}
            </SellDealClose>
          </SellDealRight>
        </SellDealBlockTop>
        <Media
          query="(min-width: 480px)"
          render={() => (
            <SellDealProgressBar>
              <SellDealProgress
                style={{
                  width: `${Math.floor(sellProgress * 100)}%`
                }}
              />
            </SellDealProgressBar>
          )}
        />
      </SellDealBlock>
    );
  };

  return (
    <div>
      <OperationView
        isClickable={props.isClickable}
        onClick={props.onClick}
        mobile={props.mobile}
        sellEnabled={isRefundable && sellProgress < 1}
      >
        {props.mobile ? null : <Time>{date}</Time>}
        <Title mobile={props.mobile}>
          {props.multiplier > 1 && `${props.multiplier}✕`}
          {props.stock.slice(0, 6)}
        </Title>

        {profit}
        {props.mobile ? (
          <DealTypeandTimeView>
            {props.bidType ? (
              <Icon d="M7.996,7.032 L7.996,0.048 L1.012,0.048 L1.012,2.043 L4.719,2.043 L0.322,6.440 L1.604,7.722 L6.001,3.325 L6.001,7.032 L7.996,7.032 Z" />
            ) : (
              <Icon d="M7.996,1.023 L7.996,8.007 L1.012,8.007 L1.012,6.011 L4.719,6.011 L0.322,1.615 L1.604,0.333 L6.001,4.730 L6.001,1.023 L7.996,1.023 Z" />
            )}
            {(!isRefundable || sellProgress >= 1 || windowWidth > 479) && (
              <TimeOnMobile>{date}</TimeOnMobile>
            )}
          </DealTypeandTimeView>
        ) : (
          <BetCount>
            <Container>
              <BetDirection>
                <BetIcon bidType={props.bidType} />
              </BetDirection>
              <BetDirectionText>
                {props.bidType ? (
                  <Gettext>Upper</Gettext>
                ) : (
                  <Gettext>lower</Gettext>
                )}
              </BetDirectionText>
            </Container>
            <BetCountText>
              <Money currency={currency}>
                {props.multiplier * props.amount}
              </Money>
            </BetCountText>
          </BetCount>
        )}
        <ProgressBar mobile={props.mobile}>
          <Progress
            mobile={props.mobile}
            style={{
              width: Math.floor(progress * 100) + "%"
            }}
          />
        </ProgressBar>
        <Media query="(max-width: 479px)" render={() => <SellDeal />} />
      </OperationView>
      <Media query="(min-width: 480px)" render={() => <SellDeal />} />
    </div>
  );
}

export function ClosedOperation(props: DealObject & DealProps) {
  if (typeof props.percent !== "number") {
    return null;
  }

  const date = moment(props.open_timestamp * 1000).format("DD/MM HH:mm:ss");
  const currency = props.tradeMode === "tournament" ? tournamentCurrency : null;

  let profit = null;
  switch (Math.sign(props.profit)) {
    case 1:
      profit = (
        <Bet mobile={props.mobile} isWinning={true}>
          <Money currency={currency}>{props.multiplier * props.profit}</Money>
        </Bet>
      );
      break;
    case -1:
      profit = (
        <Bet mobile={props.mobile} isWinning={false}>
          <Money currency={currency}>{props.multiplier * props.profit}</Money>
        </Bet>
      );
      break;
    default:
      profit = (
        <RefundContainer>
          <Refund />
        </RefundContainer>
      );
      break;
  }

  return (
    <OperationView
      isClickable={props.isClickable}
      onClick={props.onClick}
      mobile={props.mobile}
    >
      {props.mobile ? null : <Time>{date}</Time>}
      <Title mobile={props.mobile}>
        {props.multiplier > 1 && `${props.multiplier}✕`}
        {props.stock.slice(0, 6)}
      </Title>

      {profit}

      {props.mobile ? (
        <DealTypeandTimeView>
          {props.bidType ? (
            <Icon d="M7.996,7.032 L7.996,0.048 L1.012,0.048 L1.012,2.043 L4.719,2.043 L0.322,6.440 L1.604,7.722 L6.001,3.325 L6.001,7.032 L7.996,7.032 Z" />
          ) : (
            <Icon d="M7.996,1.023 L7.996,8.007 L1.012,8.007 L1.012,6.011 L4.719,6.011 L0.322,1.615 L1.604,0.333 L6.001,4.730 L6.001,1.023 L7.996,1.023 Z" />
          )}
          <TimeOnMobile>{date}</TimeOnMobile>
        </DealTypeandTimeView>
      ) : (
        <BetCount>
          <Container>
            <BetDirection>
              <BetIcon bidType={props.bidType} />
            </BetDirection>
            <BetDirectionText>
              {props.bidType ? (
                <Gettext>Upper</Gettext>
              ) : (
                <Gettext>lower</Gettext>
              )}
            </BetDirectionText>
          </Container>
          <BetCountText>
            <Money currency={currency}>{props.multiplier * props.amount}</Money>
          </BetCountText>
        </BetCount>
      )}
    </OperationView>
  );
}

function Operation(props) {
  return props.profit == null && props.time != null ? (
    <OpenOperation {...props} />
  ) : (
    <ClosedOperation {...props} />
  );
}

type Props = {
  tradeMode: "tournament" | "binary",
  deals: Array<DealObject>,
  now: number,
  point: {
    date: number,
    stock: string,
    value: number
  },
  pointsByStock: Object | void,
  // pointsByStock: { USDJPY: <point>, USDWTV: <point>, ... }
  mobile?: boolean,
  isOperationClickable: boolean,
  onOperationClick: (operation: DealObject) => void
};

function TradeHistory(props: Props) {
  const activeDealsLength = props.deals.filter(
    deal => deal.profit == null && deal.time != null
  ).length;

  const deals = props.deals.slice(
    0,
    activeDealsLength > 10 ? activeDealsLength : 10
  );

  if (props.deals.length > 0) {
    return deals
      .reduce(dealDuplicatesReducer, [])
      .map((deal, i) => (
        <Operation
          key={deal.id || i}
          {...deal}
          tradeMode={props.tradeMode}
          now={props.now}
          point={props.point}
          pointsByStock={props.pointsByStock}
          mobile={props.mobile}
          isClickable={
            props.isOperationClickable ? props.isOperationClickable(deal) : true
          }
          onClick={() => props.onOperationClick(deal)}
          refundClickHandler={() => dealCloser(deal, props.refundClickHandler)}
          refundFetching={props.refundFetching}
          gettext={props.gettext}
        />
      ));
  }

  return (
    <NoOpenTrades>
      <Gettext>You have no open trades</Gettext>
    </NoOpenTrades>
  );
}

export default withGettext(TradeHistory);



// WEBPACK FOOTER //
// ./src/components/TradeControls/History.js